import styled, { css, keyframes } from 'styled-components';
import { NavLink } from 'react-router-dom';

import { hexToRgbA } from 'utils/colors';

type BlockProps = {
  $isDisabled: boolean;
  $isSelected?: boolean;
  $disableReason: 'none' | 'blocked' | 'subscriptionSlotInactive';
};

/** Soft ring that expands slightly past the community slot (box-shadow, no filled disc). */
const communityRipplePulse = (ringColor: string) => keyframes`
  0% {
    transform: translate(-50%, -50%) scale(1);
    box-shadow: 0 0 0 0 ${hexToRgbA(ringColor, 0.5)};
  }
  70% {
    transform: translate(-50%, -50%) scale(1.08);
    box-shadow: 0 0 0 18px ${hexToRgbA(ringColor, 0)};
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    box-shadow: 0 0 0 0 ${hexToRgbA(ringColor, 0)};
  }
`;

export const StyledRipple = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  pointer-events: none;
  z-index: -1;
  background: transparent;
  transform: translate(-50%, -50%) scale(1);
  animation: ${({ theme }) => communityRipplePulse(theme.color.brightTurquoise)}
    2.4s ease-out infinite;
  animation-fill-mode: backwards;
`;

export const StyledBlock = styled(NavLink)<BlockProps>`
  text-decoration: none;
  width: 54px;
  height: 54px;
  border: 3px solid transparent;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: visible;
  margin: 2px 0;
  box-sizing: border-box;
  pointer-events: ${({ $isDisabled }) => ($isDisabled ? 'none' : 'all')};
  transition: border-color 0.22s ease;

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      background: transparent;
    `}

  ${({ $isDisabled, theme }) =>
    !$isDisabled &&
    css`
      &:hover {
        border-color: ${theme.color.brightTurquoise};
      }
    `}

  ${({ $isDisabled, $isSelected, theme }) =>
    !$isDisabled &&
    $isSelected &&
    css`
      border-color: ${theme.color.brightTurquoise};
      isolation: isolate;
    `}

  &.active {
    ${({ $isDisabled, $isSelected, theme }) =>
      !$isDisabled &&
      !$isSelected &&
      css`
        border-color: ${theme.color.brightTurquoise};
      `}
  }

  @media (prefers-reduced-motion: reduce) {
    ${StyledRipple} {
      animation: none;
      box-shadow: 0 0 0 2px
        ${({ theme }) => hexToRgbA(theme.color.brightTurquoise, 0.35)};
    }
  }
`;
